import { useEffect, useState, useCallback } from 'react';

import ClearIcon from '@mui/icons-material/Clear';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';

import * as Sentry from '@sentry/react';

import axios from '../axios';
import ButtonWithText from '../components/ButtonWithText';
import UserList from '../components/UserList';
import AuthService from '../helpers/auth/AuthService';
import { validateAndCorrectUri, isValidUri } from '../helpers/UriValidator';
import HomeLayout from '../layouts/HomeLayout';
import { User } from '../types/User';

export default function Settings() {
  const [auth, setAuth] = useState(AuthService.getCurrentUser());
  const [callbackURI, setCallbackURI] = useState('');
  const [redirectURI, setRedirectURI] = useState('');
  const [users, setUsers] = useState<Array<User>>([]);
  const [snackMessage, setSnackMessage] = useState('');
  const [callbackError, setCallbackError] = useState('');
  const [redirectError, setRedirectError] = useState('');
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    open: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });
  Sentry.setUser({ email: auth.email });

  const handleErrClose = (
    event: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === 'clickaway') {
      return;
    }

    setSnackMessage('');
  };

  const fetchUsers = useCallback(() => {
    const config = {
      headers: { Authorization: `Bearer ${auth.access_token}` },
    };

    axios
      .get('/user/list', config)
      .then((res) => {
        setUsers(res.data);
      })
      .catch((err) => {
        setSnackMessage(err.response?.data || 'Error fetching users');
      });
  }, [auth.access_token, setSnackMessage]);

  useEffect(() => {
    setAuth(AuthService.getCurrentUser());

    const config = {
      headers: { Authorization: `Bearer ${auth.access_token}` },
    };

    axios.get('/service/' + auth.service_id, config).then((res) => {
      if (res.status === 200) {
        setCallbackURI(res.data.CallbackURI);
        setRedirectURI(res.data.RedirectURI);
      }
    });

    fetchUsers();
  }, [auth.access_token, auth.service_id, fetchUsers]);

  const handleCallbackURIChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCallbackURI(value);
    setCallbackError('');

    if (value) {
      try {
        if (!isValidUri(validateAndCorrectUri(value))) {
          setCallbackError('Please enter a valid URI');
        }
      } catch (error) {
        setCallbackError('Please enter a valid domain (e.g., example.com)');
      }
    }
  };

  const handleRedirectURIChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setRedirectURI(value);
    setRedirectError('');

    if (value) {
      try {
        if (!isValidUri(validateAndCorrectUri(value))) {
          setRedirectError('Please enter a valid URI');
        }
      } catch (error) {
        setRedirectError('Please enter a valid domain (e.g., example.com)');
      }
    }
  };

  const handleClearCallback = () => {
    setCallbackURI('');
    setCallbackError('');

    const config = {
      headers: { Authorization: `Bearer ${auth.access_token}` },
    };

    const payload = {
      CallbackURI: '',
      RedirectURI: redirectURI,
      service_id: auth.service_id,
    };

    console.log('Clearing Callback URI - Request payload:', payload);

    axios
      .put('/service', payload, config)
      .then((res) => {
        console.log('Clear Callback Response:', res.data);
        if (res.status === 200) {
          setSnackMessage('Callback URI Cleared');
        } else {
          setSnackMessage('Error clearing Callback URI');
        }
      })
      .catch((err) => {
        console.error('Clear Callback Error:', err.response?.data);
        setSnackMessage(err.response?.data || 'Error clearing URI');
        // Revert the UI if the server update fails
        setCallbackURI(callbackURI);
      });
  };

  const handleClearRedirect = () => {
    setRedirectURI('');
    setRedirectError('');

    const config = {
      headers: { Authorization: `Bearer ${auth.access_token}` },
    };

    const payload = {
      CallbackURI: callbackURI,
      RedirectURI: '',
      service_id: auth.service_id,
    };

    console.log('Clearing Redirect URI - Request payload:', payload);

    axios
      .put('/service', payload, config)
      .then((res) => {
        console.log('Clear Redirect Response:', res.data);
        if (res.status === 200) {
          setSnackMessage('Redirect URI Cleared');
        } else {
          setSnackMessage('Error clearing Redirect URI');
        }
      })
      .catch((err) => {
        console.error('Clear Redirect Error:', err.response?.data);
        setSnackMessage(err.response?.data || 'Error clearing URI');
        // Revert the UI if the server update fails
        setRedirectURI(redirectURI);
      });
  };

  const updateCallbackURI = () => {
    try {
      const correctedUri = validateAndCorrectUri(callbackURI);
      if (!isValidUri(correctedUri)) {
        setCallbackError('Please enter a valid URI');
        return;
      }

      const config = {
        headers: { Authorization: `Bearer ${auth.access_token}` },
      };

      const payload = {
        CallbackURI: correctedUri,
        RedirectURI: redirectURI,
        service_id: auth.service_id,
      };

      console.log('Updating Callback URI - Request payload:', payload);

      axios
        .put('/service', payload, config)
        .then((res) => {
          console.log('Update Callback Response:', res.data);
          if (res.status === 200) {
            setCallbackURI(correctedUri);
            setSnackMessage('Callback URI Updated');
          } else {
            setSnackMessage('Error updating Callback URI');
          }
        })
        .catch((err) => {
          console.error('Update Callback Error:', err.response?.data);
          setSnackMessage(err.response?.data || 'Error updating URI');
        });
    } catch (error) {
      setCallbackError('Please enter a valid domain (e.g., example.com)');
    }
  };

  const updateRedirectURI = () => {
    try {
      const correctedUri = validateAndCorrectUri(redirectURI);
      if (!isValidUri(correctedUri)) {
        setRedirectError('Please enter a valid URI');
        return;
      }

      const config = {
        headers: { Authorization: `Bearer ${auth.access_token}` },
      };

      const payload = {
        CallbackURI: callbackURI,
        RedirectURI: correctedUri,
        service_id: auth.service_id,
      };

      console.log('Updating Redirect URI - Request payload:', payload);

      axios
        .put('/service', payload, config)
        .then((res) => {
          console.log('Update Redirect Response:', res.data);
          if (res.status === 200) {
            setRedirectURI(correctedUri);
            setSnackMessage('Redirect URI Updated');
          } else {
            setSnackMessage('Error updating Redirect URI');
          }
        })
        .catch((err) => {
          console.error('Update Redirect Error:', err.response?.data);
          setSnackMessage(err.response?.data || 'Error updating URI');
        });
    } catch (error) {
      setRedirectError('Please enter a valid domain (e.g., example.com)');
    }
  };

  const getUserSignupLink = () => {
    axios
      .post(
        'user/signup_token',
        {
          duration_seconds: 604800,
        },
        {
          headers: {
            Authorization: `Bearer ${auth.access_token}`,
          },
        }
      )
      .then((res) => {
        if (res.status === 201) {
          navigator.clipboard.writeText(res.data.url);
          setSnackMessage('User Signup Link Copied');
        }
      })
      .catch((err) => {
        if (err.response) {
          setSnackMessage(err.response.data);
        } else {
          setSnackMessage('Unknown error occurred.');
        }
      });
  };

  const handleConfirmClose = () => {
    setConfirmDialog({
      ...confirmDialog,
      open: false,
    });
  };

  const handleConfirm = () => {
    confirmDialog.onConfirm();
    handleConfirmClose();
  };

  const showConfirmDialog = (
    title: string,
    message: string,
    onConfirm: () => void
  ) => {
    setConfirmDialog({
      open: true,
      title,
      message,
      onConfirm,
    });
  };

  const initiateCallbackClear = () => {
    showConfirmDialog(
      'Clear Callback URI',
      'Are you sure you want to clear the Callback URI?',
      handleClearCallback
    );
  };

  const initiateRedirectClear = () => {
    showConfirmDialog(
      'Clear Redirect URI',
      'Are you sure you want to clear the Redirect URI?',
      handleClearRedirect
    );
  };

  return (
    <HomeLayout>
      <Grid item xs={12}>
        <Typography component='h4' variant='h4'>
          Integration Settings
        </Typography>
      </Grid>
      <Paper
        sx={{
          p: 2,
          display: 'flex',
          mt: 2,
        }}
      >
        <Grid container>
          <Grid item container direction={'row'} spacing={2} mt={0}>
            <Grid item xs={10}>
              <TextField
                id='callback-uri'
                label='Callback URI'
                variant='outlined'
                value={callbackURI}
                onChange={handleCallbackURIChange}
                error={!!callbackError}
                helperText={callbackError}
                InputProps={{
                  endAdornment: callbackURI && (
                    <InputAdornment position='end'>
                      <IconButton
                        aria-label='clear callback uri'
                        onClick={initiateCallbackClear}
                        edge='end'
                        size='small'
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: '100%',
                }}
              />
            </Grid>
            <Grid item xs={2} alignItems='stretch' style={{ display: 'flex' }}>
              <ButtonWithText
                label='Update'
                handleClick={updateCallbackURI}
                disabled={!!callbackError}
                tooltipText={callbackError || undefined}
              />
            </Grid>
          </Grid>
          <Grid item container direction={'row'} spacing={2} mt={0}>
            <Grid item xs={10}>
              <TextField
                id='redirect-uri'
                label='Redirect URI'
                variant='outlined'
                value={redirectURI}
                onChange={handleRedirectURIChange}
                error={!!redirectError}
                helperText={redirectError}
                InputProps={{
                  endAdornment: redirectURI && (
                    <InputAdornment position='end'>
                      <IconButton
                        aria-label='clear redirect uri'
                        onClick={initiateRedirectClear}
                        edge='end'
                        size='small'
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: '100%',
                }}
              />
            </Grid>
            <Grid item xs={2} alignItems='stretch' style={{ display: 'flex' }}>
              <ButtonWithText
                label='Update'
                handleClick={updateRedirectURI}
                disabled={!!redirectError}
                tooltipText={redirectError || undefined}
              />
            </Grid>
          </Grid>
        </Grid>
      </Paper>
      <Grid container alignItems='center' justifyContent='space-between' mt={3}>
        <Grid item>
          <Typography variant='h5' component='h2'>
            Users
          </Typography>
        </Grid>
        <Grid item>
          <ButtonWithText
            label='Get User Signup Link'
            handleClick={getUserSignupLink}
          />
        </Grid>
      </Grid>
      <UserList users={users} onUserUpdated={fetchUsers} />
      <Snackbar
        open={snackMessage !== ''}
        autoHideDuration={3000}
        message={snackMessage}
        onClose={handleErrClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        sx={{
          '& .MuiSnackbarContent-root': {
            backgroundColor: '#282583',
            minWidth: 'auto',
            padding: '6px 16px',
          },
        }}
      />

      <Dialog
        open={confirmDialog.open}
        onClose={handleConfirmClose}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
      >
        <DialogTitle id='alert-dialog-title'>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText id='alert-dialog-description'>
            {confirmDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmClose}>Cancel</Button>
          <Button onClick={handleConfirm} autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </HomeLayout>
  );
}
